import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Grid3X3, Search, AlertCircle, MapPin } from 'lucide-react';
import { clinicApi } from '../services/clinicApi';
import { procedureApi } from '@features/procedures/services/procedureApi';
import { searchCustomers, getCustomerLocations, geocodeLocation } from '@features/coverage/services/customerApi';
import apiClient from '@shared/services/apiClient';
import ClientRelationshipMap from '@features/coverage/components/ClientRelationshipMap';
import ClinicDetailPanel from '../components/ClinicDetailPanel';
import CollapsibleSection from '../components/CollapsibleSection';
import ClinicMatrixGrid from '../components/ClinicMatrixGrid';
import { useDebounce } from '@hooks/useDebounce';
import type { ClinicWithDistance, ClinicCapabilityType, ClinicCategoryType } from '../types/clinic';
import type { ProcedureWithGroupDto } from '@features/procedures/types/procedure';
import type { Customer, CustomerLocation, AccountStatus } from '@features/coverage/types/clientClinic';
import { withXmId } from '@shared/utils/xmId';
import './ClinicMatrixPage.css';
import RadiusInput from '@shared/components/common/RadiusInput';

type InputMode = 'customer' | 'address';
type GroupKey = 'related' | 'houseAccounts' | 'labcorp' | 'nearby';

function formatLocationAddress(location: CustomerLocation): string | null {
  const parts: string[] = [];
  if (location.street) parts.push(location.street);
  if (location.suite) parts.push(location.suite);
  const cityStateZip: string[] = [];
  if (location.city) cityStateZip.push(location.city);
  if (location.state) cityStateZip.push(location.state);
  if (location.zip) cityStateZip.push(location.zip);
  if (cityStateZip.length > 0) parts.push(cityStateZip.join(', '));
  return parts.length > 0 ? parts.join(', ') : null;
}

function hasCapability(clinic: ClinicWithDistance, capabilityId: number | undefined): boolean {
  if (capabilityId == null || !clinic.capabilityTypeIds) return false;
  return clinic.capabilityTypeIds.split(',').map(Number).includes(capabilityId);
}

export default function ClinicMatrixPage() {
  // Shared state
  const [inputMode, setInputMode] = useState<InputMode>('customer');
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [geoPoint, setGeoPoint] = useState<{ lat: number; lon: number; label: string } | null>(null);
  const [selectedClinicId, setSelectedClinicId] = useState<number | undefined>(undefined);
  const [activeTab, setActiveTab] = useState('info');

  // Customer mode state
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<CustomerLocation | null>(null);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  // Address mode state
  const [addressInput, setAddressInput] = useState('');
  const [pendingAddress, setPendingAddress] = useState('');
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Grid section expanded state — all start collapsed
  const [expandedSections, setExpandedSections] = useState({
    related: false,
    houseAccounts: false,
    labcorp: false,
    nearby: false,
  });

  // Filter state
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [selectedCapabilities, setSelectedCapabilities] = useState<Set<number>>(new Set());
  const [selectedProcedures, setSelectedProcedures] = useState<Set<number>>(new Set());
  const [procedureSearch, setProcedureSearch] = useState('');

  // Legend group filter — null means "all visible"
  const [visibleGroups, setVisibleGroups] = useState<Set<GroupKey> | null>(null);

  const debouncedCustomerSearch = useDebounce(customerSearch, 300);

  // Customer search query
  const {
    data: customers = [],
    isLoading: isSearchingCustomers,
  } = useQuery({
    queryKey: ['matrixCustomerSearch', debouncedCustomerSearch],
    queryFn: () => searchCustomers(debouncedCustomerSearch),
    enabled: inputMode === 'customer' && debouncedCustomerSearch.length >= 2 && !selectedCustomer,
  });

  // Customer locations query
  const {
    data: locations = [],
  } = useQuery({
    queryKey: ['matrixCustomerLocations', selectedCustomer?.id],
    queryFn: () => getCustomerLocations(selectedCustomer!.id),
    enabled: inputMode === 'customer' && !!selectedCustomer,
  });

  // Nearby clinics query
  const {
    data: nearbyData = [],
    isFetching: loadingClinics,
  } = useQuery({
    queryKey: ['matrixNearbyClinics', geoPoint?.lat, geoPoint?.lon, radiusMiles],
    queryFn: () => clinicApi.getNearbyClinics(geoPoint!.lat, geoPoint!.lon, radiusMiles, 50),
    enabled: geoPoint !== null,
  });

  // Relationships query (customer mode only)
  const {
    data: relationships = [],
  } = useQuery({
    queryKey: ['matrixRelationships', selectedLocation?.id],
    queryFn: async () => {
      if (!selectedLocation) return [];
      const response = await apiClient.get(`/clinic-location-client-accounts/location/${selectedLocation.id}`);
      return response.data;
    },
    enabled: inputMode === 'customer' && !!selectedLocation,
  });

  // Related clinic details fetched by id — NOT limited to the nearby radius/top-50,
  // so a related clinic always shows even when it isn't among the closest clinics.
  // Sorted so the same id-set always produces the same query key.
  const relatedClinicIds = useMemo(
    () =>
      Array.from(new Set((relationships as { clinicId: number }[]).map(r => r.clinicId)))
        .sort((a, b) => a - b),
    [relationships]
  );

  const { data: relatedClinicData } = useQuery({
    queryKey: ['matrixRelatedClinics', relatedClinicIds.join(','), geoPoint?.lat, geoPoint?.lon],
    queryFn: () => clinicApi.getClinicsByIds(relatedClinicIds, geoPoint?.lat, geoPoint?.lon),
    enabled: inputMode === 'customer' && relatedClinicIds.length > 0,
  });

  // Category types for filter checkboxes
  const { data: categoryTypes = [] } = useQuery({
    queryKey: ['categoryTypes'],
    queryFn: () => clinicApi.getCategoryTypes(),
    staleTime: 30 * 60 * 1000,
  });

  // Capability types for filter checkboxes
  const { data: capabilityTypes = [] } = useQuery({
    queryKey: ['capabilityTypes'],
    queryFn: () => clinicApi.getCapabilityTypes(),
    staleTime: 30 * 60 * 1000,
  });

  // Procedures for filter checkboxes — server returns only procedures mapped to a service group
  const { data: procedureTypes = [] } = useQuery({
    queryKey: ['procedures', 'mappedOnly'],
    queryFn: () => procedureApi.getProcedures(undefined, true),
    staleTime: 30 * 60 * 1000,
  });

  // Alphabetical copies for the filter chip lists (SLV2-2915) — the lookup
  // endpoints return arbitrary table order (no ORDER BY in the procs).
  const sortedCategoryTypes = useMemo(
    () =>
      [...categoryTypes].sort((a: ClinicCategoryType, b: ClinicCategoryType) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ),
    [categoryTypes]
  );

  const sortedCapabilityTypes = useMemo(
    () =>
      [...capabilityTypes].sort((a: ClinicCapabilityType, b: ClinicCapabilityType) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      ),
    [capabilityTypes]
  );

  const sortedProcedureTypes = useMemo(
    () =>
      [...procedureTypes].sort((a: ProcedureWithGroupDto, b: ProcedureWithGroupDto) =>
        a.effectiveName.localeCompare(b.effectiveName, undefined, { sensitivity: 'base' })
      ),
    [procedureTypes]
  );

  // Procedures shown as chips — full list by default, narrowed by the search box
  const displayedProcedures = useMemo(() => {
    const term = procedureSearch.trim().toLowerCase();
    if (!term) return sortedProcedureTypes;
    return sortedProcedureTypes.filter((proc: ProcedureWithGroupDto) =>
      proc.effectiveName.toLowerCase().includes(term) ||
      proc.procedureCode.toLowerCase().includes(term) ||
      (proc.groupName?.toLowerCase().includes(term) ?? false)
    );
  }, [sortedProcedureTypes, procedureSearch]);

  // Bucket capability IDs (derived dynamically from capabilityTypes).
  // 'labcorp_site' marks a clinic that IS a LabCorp collection site — distinct
  // from 'labcorp' ("Labcorp Courier" = the clinic has a LabCorp courier pickup).
  const labCorpCapabilityId = useMemo(
    () => capabilityTypes.find((c: ClinicCapabilityType) => c.key === 'labcorp_site')?.id,
    [capabilityTypes]
  );

  // 'one_source_account' marks a clinic holding a general 1Source/Examinetics
  // account — these populate the "One Source Accounts" bucket (formerly
  // labeled "House Accounts"; renamed per Morgan's team 2026-08-04 — that
  // verbiage reads as the clinic's own house account / drug-screen accounts).
  const houseAccountCapabilityId = useMemo(
    () => capabilityTypes.find((c: ClinicCapabilityType) => c.key === 'one_source_account')?.id,
    [capabilityTypes]
  );

  // Filter toggle handlers
  const toggleCategory = (name: string) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const toggleCapability = (id: number) => {
    setSelectedCapabilities(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleProcedure = (id: number) => {
    setSelectedProcedures(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Classification logic (with capability filtering).
  // Bucket precedence: related > house account > labcorp > nearby.
  // Related clinics come from their own by-ids query so they are never lost to
  // the nearby radius / top-50 cut; everything else classifies the nearby pool.
  const { relatedClinics, houseAccountClinics, labCorpClinics, nearbyClinics, markerCategories, relationshipStatuses } = useMemo(() => {
    // Chip filters (AND logic: clinic must match ALL selected)
    const matchesFilters = (clinic: ClinicWithDistance): boolean => {
      if (selectedCategories.size > 0) {
        if (!clinic.categoryNames) return false;
        const clinicCats = clinic.categoryNames.split(', ').map(s => s.trim());
        for (const reqCat of selectedCategories) {
          if (!clinicCats.includes(reqCat)) return false;
        }
      }
      if (selectedCapabilities.size > 0) {
        if (!clinic.capabilityTypeIds) return false;
        const clinicCaps = new Set(clinic.capabilityTypeIds.split(',').map(Number));
        for (const reqCap of selectedCapabilities) {
          if (!clinicCaps.has(reqCap)) return false;
        }
      }
      if (selectedProcedures.size > 0) {
        if (!clinic.procedureIds) return false;
        const clinicProcs = new Set(
          clinic.procedureIds
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
            .map(Number)
        );
        for (const reqProc of selectedProcedures) {
          if (!clinicProcs.has(reqProc)) return false;
        }
      }
      return true;
    };

    const relStatuses = new Map<number, AccountStatus>();
    const relatedIds = new Set<number>();
    if (inputMode === 'customer') {
      (relationships as { clinicId: number; accountStatus: AccountStatus }[]).forEach(r => {
        relatedIds.add(r.clinicId);
        relStatuses.set(r.clinicId, r.accountStatus);
      });
    }

    // Related bucket: from the dedicated by-ids query (chip filters still apply).
    // While that query is in flight, fall back to the related clinics already
    // present in the nearby pool so they don't vanish from every bucket.
    const relatedSource: ClinicWithDistance[] = inputMode === 'customer'
      ? (relatedClinicData ?? (nearbyData ?? []).filter((c: ClinicWithDistance) => relatedIds.has(c.id)))
      : [];
    const related = relatedSource.filter(matchesFilters);

    // Remaining buckets: nearby pool, minus anything already shown as related
    const nonRelated = (nearbyData ?? [])
      .filter((c: ClinicWithDistance) => !relatedIds.has(c.id))
      .filter(matchesFilters);

    const houseList = nonRelated.filter(c => hasCapability(c, houseAccountCapabilityId));
    const afterHouse = nonRelated.filter(c => !hasCapability(c, houseAccountCapabilityId));
    const labcorpList = afterHouse.filter(c => hasCapability(c, labCorpCapabilityId));
    const nearbyList = afterHouse.filter(c => !hasCapability(c, labCorpCapabilityId));

    const cats = new Map<number, 'related' | 'houseAccount' | 'nearby' | 'labcorp'>();
    related.forEach(c => cats.set(c.id, 'related'));
    houseList.forEach(c => cats.set(c.id, 'houseAccount'));
    labcorpList.forEach(c => cats.set(c.id, 'labcorp'));
    nearbyList.forEach(c => cats.set(c.id, 'nearby'));

    return {
      relatedClinics: related,
      houseAccountClinics: houseList,
      labCorpClinics: labcorpList,
      nearbyClinics: nearbyList,
      markerCategories: cats,
      relationshipStatuses: relStatuses,
    };
  }, [inputMode, nearbyData, relationships, relatedClinicData, selectedCategories, selectedCapabilities, selectedProcedures, labCorpCapabilityId, houseAccountCapabilityId]);

  // Always keep all sections visible (even when empty)

  // All clinics combined for map
  const allClinicsForMap = useMemo(
    () => [...relatedClinics, ...houseAccountClinics, ...labCorpClinics, ...nearbyClinics],
    [relatedClinics, houseAccountClinics, labCorpClinics, nearbyClinics],
  );

  // Mode switch handler
  const handleModeChange = useCallback((mode: InputMode) => {
    setInputMode(mode);
    setGeoPoint(null);
    setSelectedClinicId(undefined);
    setCustomerSearch('');
    setSelectedCustomer(null);
    setSelectedLocation(null);
    setAddressInput('');
    setPendingAddress('');
    setGeocodeError(null);
    setSelectedCategories(new Set());
    setSelectedCapabilities(new Set());
    setSelectedProcedures(new Set());
    setProcedureSearch('');
  }, []);

  // Customer selection
  const handleCustomerSelect = useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch('');
    setSelectedLocation(null);
    setGeoPoint(null);
    setSelectedClinicId(undefined);
    setGeocodeError(null);
  }, []);

  // Location selection
  const handleLocationSelect = useCallback(async (location: CustomerLocation) => {
    setSelectedLocation(location);
    setGeocodeError(null);
    setSelectedClinicId(undefined);

    if (location.latitude && location.longitude) {
      setGeoPoint({ lat: location.latitude, lon: location.longitude, label: location.name });
    } else {
      try {
        const geocoded = await geocodeLocation(location.customerId, location.id);
        setSelectedLocation(geocoded);
        if (geocoded.latitude && geocoded.longitude) {
          setGeoPoint({ lat: geocoded.latitude, lon: geocoded.longitude, label: geocoded.name });
        } else {
          setGeocodeError('Unable to geocode this location. Please ensure it has a valid address.');
        }
      } catch {
        setGeocodeError('Unable to geocode this location. Please ensure it has a valid address.');
      }
    }
  }, []);

  // Address search
  const handleAddressSearch = useCallback(async () => {
    const trimmed = addressInput.trim();
    if (!trimmed) return;
    if (trimmed === pendingAddress && geoPoint) return;

    setIsGeocoding(true);
    setGeocodeError(null);
    setGeoPoint(null);
    setSelectedClinicId(undefined);

    try {
      const coords = await clinicApi.geocodeAddress(trimmed);
      setGeoPoint({ lat: coords.latitude, lon: coords.longitude, label: trimmed });
      setPendingAddress(trimmed);
    } catch {
      setGeocodeError('Could not find that address. Try being more specific (e.g. include city and state).');
    } finally {
      setIsGeocoding(false);
    }
  }, [addressInput, pendingAddress, geoPoint]);

  const handleAddressKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleAddressSearch();
  };

  // Clear customer selection
  const handleClearCustomer = useCallback(() => {
    setSelectedCustomer(null);
    setSelectedLocation(null);
    setGeoPoint(null);
    setSelectedClinicId(undefined);
    setCustomerSearch('');
    setGeocodeError(null);
  }, []);

  // Clinic selection
  const handleClinicSelect = useCallback((clinicId: number) => {
    setSelectedClinicId(clinicId);
    setActiveTab('info');
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedClinicId(undefined);
  }, []);

  // Section toggle
  const toggleSection = useCallback((section: 'related' | 'houseAccounts' | 'labcorp' | 'nearby') => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Legend group filter toggle
  // - plain click: solo that group (or clear if it's already the only one selected)
  // - ctrl/meta click: add/remove from multi-select
  const handleLegendClick = useCallback((group: GroupKey, multiSelect: boolean) => {
    setVisibleGroups(prev => {
      if (multiSelect) {
        // Add or remove from existing selection
        const next = new Set<GroupKey>(prev ?? (['related', 'houseAccounts', 'labcorp', 'nearby'] as GroupKey[]));
        if (next.has(group)) {
          next.delete(group);
          return next.size === 0 || next.size === 4 ? null : next;
        } else {
          next.add(group);
          return next.size === 4 ? null : next;
        }
      } else {
        // Solo: if already the sole selection, clear back to all
        if (prev?.size === 1 && prev.has(group)) return null;
        return new Set<GroupKey>([group]);
      }
    });
  }, []);

  const isGroupVisible = useCallback((group: GroupKey): boolean => {
    return visibleGroups === null || visibleGroups.has(group);
  }, [visibleGroups]);

  // Show customer autocomplete dropdown
  const showCustomerDropdown = inputMode === 'customer' && customerSearch.length >= 2 && !selectedCustomer;

  return (
    <div className="matrix-page">
      {/* Header */}
      <div className="matrix-page-header">
        <Grid3X3 size={28} />
        <h1>Clinic Matrix</h1>
      </div>

      {/* Mode toggle */}
      <div className="matrix-mode-toggle">
        <button
          type="button"
          className={inputMode === 'customer' ? 'active' : ''}
          onClick={() => handleModeChange('customer')}
        >
          Customer Location
        </button>
        <button
          type="button"
          className={inputMode === 'address' ? 'active' : ''}
          onClick={() => handleModeChange('address')}
        >
          Address
        </button>
      </div>

      {/* Top row: search panel + map */}
      <div className="matrix-top-row">
        <div className="matrix-search-panel">
          {inputMode === 'customer' ? (
            <>
              {/* Customer + Location selection */}
              {!selectedCustomer ? (
                <div className="matrix-field-group">
                  <label>Customer</label>
                  <div className="matrix-autocomplete-wrapper">
                    <input
                      type="text"
                      placeholder="Search customers..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      aria-label="Search customers"
                    />
                    {showCustomerDropdown && (
                      <div className="matrix-autocomplete-dropdown">
                        {isSearchingCustomers ? (
                          <div className="matrix-autocomplete-loading">Searching...</div>
                        ) : customers.length > 0 ? (
                          customers.map((customer: Customer) => (
                            <div
                              key={customer.id}
                              className="matrix-autocomplete-item"
                              onClick={() => handleCustomerSelect(customer)}
                            >
                              <div className="matrix-autocomplete-item-name">{withXmId(customer.name, customer.xmId)}</div>
                              {customer.accountNumber && (
                                <div className="matrix-autocomplete-item-detail">
                                  Account: {customer.accountNumber}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="matrix-autocomplete-empty">No customers found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : !selectedLocation ? (
                <>
                  {/* Selected customer chip + location dropdown */}
                  <div className="matrix-selected-location">
                    <div className="matrix-selected-location-info">
                      <div className="matrix-selected-customer-name">Customer</div>
                      <div className="matrix-selected-location-name">{selectedCustomer.name}</div>
                    </div>
                    <button
                      type="button"
                      className="matrix-clear-btn"
                      onClick={handleClearCustomer}
                    >
                      Clear
                    </button>
                  </div>
                  <div className="matrix-field-group">
                    <label>Location</label>
                    <select
                      value=""
                      onChange={(e) => {
                        const loc = locations.find(l => l.id === Number(e.target.value));
                        if (loc) handleLocationSelect(loc);
                      }}
                      aria-label="Select location"
                    >
                      <option value="">Select a location...</option>
                      {locations.map((loc: CustomerLocation) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}{loc.city && loc.state ? ` - ${loc.city}, ${loc.state}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  {/* Selected location display */}
                  <div className="matrix-selected-location">
                    <div className="matrix-selected-location-info">
                      <div className="matrix-selected-customer-name">{selectedCustomer.name}</div>
                      <div className="matrix-selected-location-name">{selectedLocation.name}</div>
                      {formatLocationAddress(selectedLocation) && (
                        <div className="matrix-selected-location-address">
                          <MapPin size={12} />
                          <span>{formatLocationAddress(selectedLocation)}</span>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="matrix-clear-btn"
                      onClick={handleClearCustomer}
                    >
                      Clear
                    </button>
                  </div>
                </>
              )}

              {/* Radius selector (customer mode) */}
              {selectedLocation && (
                <div className="matrix-field-group">
                  <label htmlFor="matrix-radius-customer">Radius (miles)</label>
                  <RadiusInput id="matrix-radius-customer" value={radiusMiles} onChange={setRadiusMiles} ariaLabel="Radius in miles" />
                </div>
              )}
            </>
          ) : (
            <>
              {/* Address mode */}
              <div className="matrix-address-row">
                <div className="matrix-field-group">
                  <label>Address</label>
                  <input
                    type="text"
                    placeholder="Enter an address, city, or zip code..."
                    value={addressInput}
                    onChange={(e) => setAddressInput(e.target.value)}
                    onKeyDown={handleAddressKeyDown}
                    aria-label="Search address"
                  />
                </div>
                <button
                  type="button"
                  className="matrix-search-btn"
                  onClick={handleAddressSearch}
                  disabled={!addressInput.trim() || isGeocoding}
                >
                  <Search size={14} />
                  {isGeocoding ? 'Searching...' : 'Search'}
                </button>
              </div>

              {/* Radius selector (address mode) */}
              <div className="matrix-field-group">
                <label htmlFor="matrix-radius-address">Radius (miles)</label>
                <RadiusInput id="matrix-radius-address" value={radiusMiles} onChange={setRadiusMiles} ariaLabel="Radius in miles" />
              </div>
            </>
          )}

          {/* Filters */}
          {(categoryTypes.length > 0 || capabilityTypes.length > 0 || procedureTypes.length > 0) && (
            <div className="matrix-filters-section">
              <div className="matrix-filters-header">
                <span className="matrix-filters-title">Filters</span>
                {(selectedCategories.size > 0 || selectedCapabilities.size > 0 || selectedProcedures.size > 0) && (
                  <button
                    type="button"
                    className="matrix-filter-clear"
                    onClick={() => {
                      setSelectedCategories(new Set());
                      setSelectedCapabilities(new Set());
                      setSelectedProcedures(new Set());
                      setProcedureSearch('');
                    }}
                  >
                    Clear all
                  </button>
                )}
              </div>
              {categoryTypes.length > 0 && (
                <div className="matrix-filter-group">
                  <div className="matrix-filter-label">
                    <span>Categories</span>
                    {selectedCategories.size > 0 && (
                      <span className="matrix-filter-count">{selectedCategories.size}</span>
                    )}
                  </div>
                  <div className="matrix-filter-chips">
                    {sortedCategoryTypes.map((cat: ClinicCategoryType) => (
                      <label key={cat.id} className="matrix-filter-chip">
                        <input
                          type="checkbox"
                          checked={selectedCategories.has(cat.name)}
                          onChange={() => toggleCategory(cat.name)}
                        />
                        <span>{cat.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {capabilityTypes.length > 0 && (
                <div className="matrix-filter-group">
                  <div className="matrix-filter-label">
                    <span>Capabilities</span>
                    {selectedCapabilities.size > 0 && (
                      <span className="matrix-filter-count">{selectedCapabilities.size}</span>
                    )}
                  </div>
                  <div className="matrix-filter-chips">
                    {sortedCapabilityTypes.map((cap: ClinicCapabilityType) => (
                      <label key={cap.id} className="matrix-filter-chip">
                        <input
                          type="checkbox"
                          checked={selectedCapabilities.has(cap.id)}
                          onChange={() => toggleCapability(cap.id)}
                        />
                        <span>{cap.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {procedureTypes.length > 0 && (
                <div className="matrix-filter-group">
                  <div className="matrix-filter-label">
                    <span>Procedures</span>
                    {selectedProcedures.size > 0 && (
                      <span className="matrix-filter-count">{selectedProcedures.size}</span>
                    )}
                  </div>
                  <div className="matrix-procedure-search">
                    <Search size={14} />
                    <input
                      type="text"
                      value={procedureSearch}
                      onChange={(e) => setProcedureSearch(e.target.value)}
                      placeholder="Search procedures..."
                      aria-label="Search procedures"
                    />
                  </div>
                  {displayedProcedures.length === 0 ? (
                    <span className="matrix-procedure-hint">No procedures match "{procedureSearch}"</span>
                  ) : (
                    <div className="matrix-filter-chips matrix-procedure-chips">
                      {displayedProcedures.map((proc: ProcedureWithGroupDto) => (
                        <label key={proc.procedureId} className="matrix-filter-chip">
                          <input
                            type="checkbox"
                            checked={selectedProcedures.has(proc.procedureId)}
                            onChange={() => toggleProcedure(proc.procedureId)}
                          />
                          <span>{proc.effectiveName}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Geocode error */}
          {geocodeError && (
            <div className="matrix-geocode-error">
              <AlertCircle size={16} />
              <span>{geocodeError}</span>
            </div>
          )}

          {/* Empty state (before search) */}
          {!geoPoint && !isGeocoding && !geocodeError && !selectedLocation && (
            <div className="matrix-empty-state">
              <MapPin size={32} strokeWidth={1.5} />
              <p>
                {inputMode === 'customer'
                  ? 'Search for a customer and select a location to find nearby clinics'
                  : 'Enter an address to find nearby clinics'}
              </p>
            </div>
          )}

          {/* Geocoding spinner */}
          {isGeocoding && (
            <div className="matrix-loading">
              <div className="loading-spinner" />
              <p>Locating address...</p>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="matrix-map-wrapper">
          <div className="matrix-map-canvas">
            {geoPoint ? (
              <ClientRelationshipMap
                customerLocation={{ latitude: geoPoint.lat, longitude: geoPoint.lon, name: geoPoint.label }}
                clinics={allClinicsForMap}
                selectedClinicId={selectedClinicId}
                onClinicSelect={handleClinicSelect}
                markerCategories={markerCategories}
                fadedGroups={visibleGroups ?? undefined}
                mapStyle={{ width: '100%', height: '100%', borderRadius: '8px' }}
              />
            ) : (
              <div className="matrix-map-placeholder">
                <MapPin size={32} strokeWidth={1.5} />
                <span>Map will appear here</span>
              </div>
            )}
          </div>
          {/* Legend strip — below the map; filterable groups are clickable buttons */}
          <div className="matrix-legend">
            <span className="legend-item legend-item-static">
              <span className="legend-swatch bg-blue-500" /> Search Center
            </span>
            {(
              [
                { group: 'related' as GroupKey, label: 'Related', swatchClass: 'bg-green-500' },
                { group: 'houseAccounts' as GroupKey, label: 'One Source Account', swatchClass: 'bg-orange-500' },
                { group: 'labcorp' as GroupKey, label: 'LabCorp', swatchClass: 'legend-swatch-labcorp' },
                { group: 'nearby' as GroupKey, label: 'Nearby', swatchClass: 'bg-red-500' },
              ] as const
            ).map(({ group, label, swatchClass }) => (
              <button
                key={group}
                type="button"
                className={`legend-item legend-item-btn${!isGroupVisible(group) ? ' legend-item-faded' : ''}${visibleGroups?.has(group) ? ' legend-item-active' : ''}`}
                onClick={(e) => handleLegendClick(group, e.ctrlKey || e.metaKey)}
                title={`Click to show only ${label} · Ctrl+click to toggle`}
              >
                <span className={`legend-swatch ${swatchClass}`} /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Work area: grids + detail panel */}
      <div className="matrix-work-area">
        <div className="matrix-grids-column">
          {!geoPoint && !isGeocoding ? (
            <div className="matrix-empty-state">
              <Grid3X3 size={48} strokeWidth={1.5} />
              <h2>Clinic Matrix</h2>
              <p>
                {inputMode === 'customer'
                  ? 'Select a customer location to see nearby and related clinics'
                  : 'Search an address to find nearby clinics'}
              </p>
            </div>
          ) : loadingClinics || isGeocoding ? (
            <div className="matrix-loading">
              <div className="loading-spinner" />
              <p>Finding nearby clinics...</p>
            </div>
          ) : (
            <>
              {/* All 4 sections — same in both customer and address mode */}
              <>
                  <div className={isGroupVisible('related') ? '' : 'matrix-section-hidden'}>
                  <CollapsibleSection
                    title="Related Clinics"
                    count={relatedClinics.length}
                    colorClass="green"
                    isExpanded={expandedSections.related}
                    onToggle={() => toggleSection('related')}
                  >
                    <ClinicMatrixGrid
                      clinics={relatedClinics}
                      selectedClinicId={selectedClinicId}
                      onClinicSelect={handleClinicSelect}
                      emptyMessage="No related clinics found for this location"
                      showRelationshipStatus={inputMode === 'customer'}
                      relationshipStatuses={relationshipStatuses}
                    />
                  </CollapsibleSection>
                  </div>

                  <div className={isGroupVisible('houseAccounts') ? '' : 'matrix-section-hidden'}>
                  <CollapsibleSection
                    title="One Source Accounts"
                    count={houseAccountClinics.length}
                    colorClass="orange"
                    isExpanded={expandedSections.houseAccounts}
                    onToggle={() => toggleSection('houseAccounts')}
                  >
                    <ClinicMatrixGrid
                      clinics={houseAccountClinics}
                      selectedClinicId={selectedClinicId}
                      onClinicSelect={handleClinicSelect}
                      emptyMessage="No house account clinics"
                    />
                  </CollapsibleSection>
                  </div>

                  <div className={isGroupVisible('labcorp') ? '' : 'matrix-section-hidden'}>
                  <CollapsibleSection
                    title="LabCorp Sites"
                    count={labCorpClinics.length}
                    colorClass="purple"
                    isExpanded={expandedSections.labcorp}
                    onToggle={() => toggleSection('labcorp')}
                  >
                    <ClinicMatrixGrid
                      clinics={labCorpClinics}
                      selectedClinicId={selectedClinicId}
                      onClinicSelect={handleClinicSelect}
                      emptyMessage="No LabCorp sites in this area"
                    />
                  </CollapsibleSection>
                  </div>

                  <div className={isGroupVisible('nearby') ? '' : 'matrix-section-hidden'}>
                  <CollapsibleSection
                    title="Nearby Clinics"
                    count={nearbyClinics.length}
                    colorClass="red"
                    isExpanded={expandedSections.nearby}
                    onToggle={() => toggleSection('nearby')}
                  >
                    <ClinicMatrixGrid
                      clinics={nearbyClinics}
                      selectedClinicId={selectedClinicId}
                      onClinicSelect={handleClinicSelect}
                      emptyMessage="No nearby clinics found within the search radius"
                    />
                  </CollapsibleSection>
                  </div>
                </>
            </>
          )}
        </div>

        {/* Detail panel */}
        {selectedClinicId ? (
          <div className="matrix-detail-column">
            <ClinicDetailPanel
              clinicId={selectedClinicId}
              onClose={handleClosePanel}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
        ) : (
          <div className="matrix-detail-column">
            <div className="matrix-detail-placeholder">
              Select a clinic to view details
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
